import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  UserIcon,
  CheckCircleIcon,
  ChartBarIcon 
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';
import CandidateResult from '../../components/admin/CandidateResult'; 

const InterviewResults = () => {
  const { interviewId } = useParams();
  const [results, setResults] = useState(null);
  const [interview, setInterview] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInterviewResults();
  }, [interviewId]);

  const fetchInterviewResults = async () => {
    try {
      setLoading(true);
      const data = await apiService.getInterviewResults(interviewId);
      console.log('Fetched interview results:', data);
      if (data) {
        setResults(data);
        setInterview(data.interview);
        
        // Auto-select first candidate if available
        if (data.results && data.results.length > 0) {
          setSelectedCandidate(data.results[0]);
        }
      } else {
        setError(data.message || 'Failed to fetch interview results');
      }
    } catch (err) {
      setError('Error fetching interview results');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateUpdate = (updatedCandidate) => {
    console.log('Parent received candidate update:', updatedCandidate);
    
    // Update the candidate in the results list
    setResults(prev => {
      const newResults = {
        ...prev,
        results: prev.results.map(candidate => 
          candidate.id === updatedCandidate.id ? updatedCandidate : candidate
        )
      };
      console.log('Updated results state:', newResults);
      return newResults;
    });
    
    // Update the selected candidate if it's the one that was updated
    if (selectedCandidate && selectedCandidate.id === updatedCandidate.id) {
      console.log('Updating selected candidate:', updatedCandidate);
      setSelectedCandidate(updatedCandidate);
    }
  };

  const calculateScore = (answers) => {
    if (!answers || answers.length === 0) return 0;
    const validScores = answers.filter(answer => answer.score !== null && answer.score !== undefined);
    if (validScores.length === 0) return 0;
    const totalScore = validScores.reduce((sum, answer) => sum + answer.score, 0);
    return Math.round(totalScore / validScores.length);
  };

  const getCompletionColor = (percentage) => {
    if (percentage === 100) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Results</h2>
          <p className="text-gray-600">{error}</p>
          <Link to="/admin/interviews" className="mt-4 btn-primary inline-block">
            Back to Interviews
          </Link>
        </div>
      </div>
    );
  }

  if (!results || !interview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Results Found</h2>
          <p className="text-gray-600">No interview results available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/admin/interviews" className="text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Interview Results: {interview.title}
            </h1>
            <p className="text-gray-600">View candidate responses and performance metrics</p>
          </div>
        </div>
      </div>

      {/* Interview Summary */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center">
            <VideoCameraIcon className="h-8 w-8 text-primary-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Duration</p>
              <p className="text-lg font-semibold">{interview.duration_minutes} min</p>
            </div>
          </div>
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Questions</p>
              <p className="text-lg font-semibold">{interview.total_questions || 0}</p>
            </div>
          </div>
          <div className="flex items-center">
            <UserIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Candidates</p>
              <p className="text-lg font-semibold">{results.total_candidates || 0}</p>
            </div>
          </div>
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-lg font-semibold">{results.completed_count || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Candidate Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Candidates List */}
        <div className="lg:col-span-1">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Candidates</h3>
            <div className="space-y-3">
              {results.results?.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setSelectedCandidate(session)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedCandidate?.id === session.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <img
                        className="h-8 w-8 rounded-full mr-3"
                        src={session.candidate?.profile_picture || 
                             `https://ui-avatars.com/api/?name=${encodeURIComponent(session.candidate?.name || 'Unknown')}&background=3b82f6&color=fff`}
                        alt={session.candidate?.name}
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {session.candidate?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {session.answered_questions || 0}/{session.total_questions || 0} answered
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${getCompletionColor(session.completion_percentage || 0)}`}>
                        {Math.round(session.completion_percentage || 0)}%
                      </p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {session.status}
                      </span>
                    </div>
                  </div>
                  
                  {/* Score display */}
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-gray-600">Overall Score:</span>
                    <span className="font-medium">
                      {session.answers && session.answers.some(answer => answer.score !== null && answer.score !== undefined) 
                        ? `${calculateScore(session.answers)}/100` 
                        : 'No Score'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Selected Candidate Details */}
        <div className="lg:col-span-2">
          {selectedCandidate ? (
            <CandidateResult 
              key={`${selectedCandidate.id}-${selectedCandidate.answers?.length || 0}`}
              candidate={selectedCandidate} 
              interview={interview} 
              onCandidateUpdate={handleCandidateUpdate}
            />
          ) : (
            <div className="card">
              <div className="text-center py-12">
                <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Candidate</h3>
                <p className="text-gray-600">
                  Choose a candidate from the list to view their interview responses
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewResults;